
/* ==========================================================================
   BAREME KILOMETRIQUE
   ========================================================================== */

export const MILEAGE_RATES = {
  0: 0.28,
  1: 0.38,
  2: 0.48,
  3: 0.58,
};

export function getMileageRate(passengers) {
  const count = Number(passengers);

  if (!Number.isInteger(count) || count < 0) {
    throw new Error("Nombre de passagers invalide.");
  }

  return MILEAGE_RATES[Math.min(count, 3)];
}

export function roundMoney(value) {
  return Math.round(
    (Number(value) + Number.EPSILON) * 100
  ) / 100;
}

function validateCount(value, label) {
  const count = Number(value);

  if (!Number.isInteger(count) || count < 0 || count > 99) {
    throw new Error(`${label} : nombre invalide.`);
  }

  return count;
}

/* ==========================================================================
   CALCUL D'UN PARCOURS
   ========================================================================== */

export async function computeMileageJourney({
  start,
  end,
  stops = [],
  initialPassengers = 0,
  computeDistance,
}) {
  if (!start?.trim() || !end?.trim()) {
    throw new Error(
      "Les adresses de départ et d'arrivée sont obligatoires."
    );
  }

  const points = [
    { address: start },
    ...stops,
    { address: end },
  ];

  let passengers = validateCount(
    initialPassengers,
    "Passagers au départ"
  );

  const legs = [];

  let totalMeters = 0;
  let totalAmount = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const origin = points[i].address.trim();
    const destination = points[i + 1].address.trim();

    if (!origin || !destination) {
      throw new Error(
        `Adresse manquante à l'étape ${i + 1}.`
      );
    }

    const rate = getMileageRate(passengers);

    const response = await computeDistance({
      originAddress: origin,
      destinationAddress: destination,
    });

    const meters = Number(response?.distance_meters);

    if (!Number.isFinite(meters) || meters < 0) {
      throw new Error(
        `Distance incorrecte : ${origin} → ${destination}.`
      );
    }

    const km = meters / 1000;
    const amount = km * rate;

    legs.push({
      origin,
      destination,
      distance_km: Number(km.toFixed(3)),
      passengers,
      rate,
      amount: roundMoney(amount),
    });

    totalMeters += meters;
    totalAmount += amount;

    // Changement de passagers à l'arrivée à l'étape.
    const next = points[i + 1];

    if (i + 1 < points.length - 1) {
      const boarding = validateCount(
        next.boarding ?? 0,
        "Montées"
      );

      const alighting = validateCount(
        next.alighting ?? 0,
        "Descentes"
      );

      if (alighting > passengers) {
        throw new Error(
          `Impossible de déposer ${alighting} passager(s) à ${destination} : seulement ${passengers} à bord.`
        );
      }

      passengers = passengers - alighting + boarding;
    }
  }

  const distanceKm = totalMeters / 1000;

  return {
    legs,
    distance_km: Number(distanceKm.toFixed(2)),
    montant_trajet: roundMoney(totalAmount),
    taux_moyen:
      distanceKm > 0
        ? Number((totalAmount / distanceKm).toFixed(4))
        : 0,
    finalPassengers: passengers,
  };
}

/* ==========================================================================
   ALLER + RETOUR
   ========================================================================== */

export async function computeCompleteMileage({
  outbound,
  inbound,
  roundTrip,
  computeDistance,
}) {
  const aller = await computeMileageJourney({
    ...outbound,
    computeDistance,
  });

  const retour = roundTrip
    ? await computeMileageJourney({
        ...inbound,
        computeDistance,
      })
    : null;

  const legs = [
    ...aller.legs,
    ...(retour?.legs || []),
  ];

  const totalKm = legs.reduce(
    (sum, leg) => sum + leg.distance_km,
    0
  );

  const totalAmount = legs.reduce(
    (sum, leg) => sum + leg.distance_km * leg.rate,
    0
  );

  return {
    aller,
    retour,
    legs,
    distance_km: Number(totalKm.toFixed(2)),
    montant_trajet: roundMoney(totalAmount),
    taux_moyen:
      totalKm > 0
        ? Number((totalAmount / totalKm).toFixed(4))
        : 0,
  };
}
